import * as React from 'react';

import moment from 'moment';

import {isToday, isYesterday} from 'nutshell-core/date-time';
import {Routing} from 'nutshell-core/routing';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {CopyRegularIcon} from 'shells/icon/svg/copy-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailSeriesIcon} from 'shells/icon/svg/email-series-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmptyFolderIcon} from 'shells/icon/svg/empty-folder-icon';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PencilRegularRenameIcon} from 'shells/icon/svg/pencil-regular-rename-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UserGroupIcon} from 'shells/icon/svg/user-group-icon';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {Percentage} from 'shells/percentage/percentage';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {Table} from 'shells/table/table';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import {HeaderWithHelp} from 'shells/table/table-header/header-with-help';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {EmailSequenceTemplateDuplicateInput} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {HelpNutshellEmailMarketing} from '../../../sales-automation/onboarding/help-email-marketing/help-nutshell-email-marketing';

import {appHistory} from '../../../../history';

import {canChangeTemplateGroup} from '../email-utils';
import {getClickTooltipText, getReplyTooltipText} from '../template-list/helpers';

import type {Template} from './types';

import styles from './group-table.module.css';

type TemplateToDuplicate = {
    id: string;
    name: string;
};

type Props = {
    templates: Template[];
    errorMessage: string | null | undefined;
    isLoading: boolean;
    onAddRowClick: () => void;
    onDelete: (templateId: string) => Promise<any>;
    fullFragmentLoading: boolean;
    setSequenceTemplateId: (arg1: string) => void;
    setShouldShowModal: (arg1: boolean) => void;
    setSequenceTemplateName: (arg1: string) => void;
    setShouldShowRenameModal: (arg1: boolean) => void;
    setShouldShowChangeGroupModal: (arg1: boolean) => void;
    setTemplateGroupId: (arg1: string) => void;
    onDuplicateTemplate: (arg1: EmailSequenceTemplateDuplicateInput) => Promise<any>;
};

export function TemplateTable(props: Props) {
    const {
        onDelete,
        onAddRowClick,
        fullFragmentLoading,
        setSequenceTemplateId,
        setShouldShowModal,
        setSequenceTemplateName,
        setShouldShowRenameModal,
        setShouldShowChangeGroupModal,
        setTemplateGroupId,
        onDuplicateTemplate,
    } = props;

    const {user} = useGetSessionUser();

    const [isConfirmingArchiveId, setIsConfirmingArchiveId] = React.useState<
        string | null | undefined
    >(null);
    const [isShowingSequenceOnboarding, setIsShowingSequenceOnboarding] =
        React.useState<boolean>(false);
    const [templateToDuplicate, setTemplateToDuplicate] = React.useState<
        TemplateToDuplicate | null | undefined
    >(null);
    const [isDuplicating, setIsDuplicating] = React.useState<boolean>(false);
    const [duplicateErrorMessage, setDuplicateErrorMessage] = React.useState<
        string | null | undefined
    >(null);

    const menuRef = React.useRef(null);

    const closeConfirmationDialog = () => setIsConfirmingArchiveId(null);
    const closeDuplicateModal = () => {
        setTemplateToDuplicate(null);
        setDuplicateErrorMessage(null);
    };

    const handleDuplicateTemplate = (name: string) => {
        if (!templateToDuplicate) return;

        setIsDuplicating(true);
        onDuplicateTemplate({id: templateToDuplicate.id, name: name})
            .then(() => {
                closeDuplicateModal();
            })
            .catch(() => {
                setDuplicateErrorMessage('Error duplicating template');
            })
            .finally(() => {
                setIsDuplicating(false);
            });
    };

    const getSerializedFilter = (templateId: string) => {
        return Routing.param({
            filter: {
                receivedEmailSequenceTemplates: {
                    data: [
                        {
                            data: templateId,
                        },
                    ],
                },
            },
        });
    };

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'linkedName',
                Cell: ({cell}: any) => {
                    return (
                        <Button
                            size='small'
                            variant='text-primary'
                            onClick={() => {
                                setSequenceTemplateId(cell.value.id);
                                setShouldShowModal(true);
                            }}
                            noPadding={true}
                        >
                            {cell.value.name}
                        </Button>
                    );
                },
            },
            {
                id: 'creatorUser',
                Header: 'Created by',
                // @ts-expect-error
                accessor: (row) => row.creatorUser,
                dataType: 'linkedEntity',
                width: 100,
            },
            {
                id: 'lastSentTime',
                Header: 'Last sent',
                // @ts-expect-error
                accessor: (row) => row.lastSentTime,
                dataType: 'component',
                width: 100,
                Cell: ({cell}: any) => {
                    const timestamp =
                        cell.value && cell.value > 0 ? moment.unix(cell.value) : undefined;
                    if (!timestamp) return null;

                    const TimestampComponent =
                        isToday(timestamp) || isYesterday(timestamp) ? (
                            <RelativeTimestamp datetime={cell.value} format='calendar' />
                        ) : (
                            <FormattedTimestamp
                                datetime={cell.value}
                                format={FormattedTimestamp.formats.longMonthDayWithYear}
                            />
                        );

                    return <span className='flex'>{TimestampComponent}</span>;
                },
            },
            {
                id: 'isShared',
                Header: 'Shared?',
                // @ts-expect-error
                accessor: (row) => row.isShared,
                dataType: 'boolean',
                width: 70,
                centerAlign: true,
                Cell: ({cell}: any) => {
                    if (cell.value) {
                        return <UserGroupIcon size={16} alignCenter={true} />;
                    }

                    // Need to return something, but no icon needed if not shared
                    return <div />;
                },
            },
            {
                id: 'messages',
                Header: 'Messages',
                // @ts-expect-error
                accessor: (row) => row.emailSequenceTemplateMessages.length,
                dataType: 'component',
                width: 80,
                centerAlign: true,
                Cell: ({cell}: any) => {
                    const Icon = cell.value > 1 ? EmailSeriesIcon : EmailIcon;

                    return (
                        <div className='flex align-center'>
                            <Icon size={14} alignCenter={true} />
                            <div className={styles['margin-left']}>{cell.value}</div>
                        </div>
                    );
                },
            },
            {
                id: 'replyRate',
                Header: 'Reply rate',
                // @ts-expect-error
                accessor: (row) => row.replyRate,
                dataType: 'component',
                width: 80,
                rightAlign: true,
                Cell: ({cell}: any) => {
                    if (fullFragmentLoading) {
                        return <LoadingText minWidth={50} maxWidth={50} height={15} />;
                    } else {
                        let display;
                        if (cell.value === null || undefined) {
                            display = <div>—</div>;
                        } else {
                            // @ts-expect-error
                            display = <Percentage value={cell.value / 100} precision={2} />;
                        }

                        return (
                            <div data-tip={getReplyTooltipText(cell.row.original)}>{display}</div>
                        );
                    }
                },
            },
            {
                id: 'clickRate',
                Header: 'Click rate',
                // @ts-expect-error
                accessor: (row) => row.clickRate,
                dataType: 'component',
                width: 80,
                rightAlign: true,
                Cell: ({cell}: any) => {
                    if (fullFragmentLoading) {
                        return <LoadingText minWidth={50} maxWidth={50} height={15} />;
                    } else {
                        let display;
                        if (cell.value === null || undefined) {
                            display = <div>—</div>;
                        } else {
                            // @ts-expect-error
                            display = <Percentage value={cell.value / 100} precision={2} />;
                        }

                        return (
                            <div data-tip={getClickTooltipText(cell.row.original)}>{display}</div>
                        );
                    }
                },
            },
            {
                id: 'pipeline',
                Header: () => (
                    <HeaderWithHelp
                        title='Pipeline'
                        onClick={() => {
                            setIsShowingSequenceOnboarding(true);
                        }}
                    />
                ),
                // @ts-expect-error
                accessor: (row) =>
                    row.emailAutomation &&
                    row.emailAutomation.entersStage &&
                    row.emailAutomation.entersStage.stageset &&
                    row.emailAutomation.entersStage.stageset.name,
                dataType: 'string',
                width: 100,
            },
            {
                id: 'action',
                Header: '',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                width: 40,
                rightAlign: true,
                disableSortBy: true,
                Cell: ({cell}: any) => {
                    const options = [];

                    if (cell.value.canEdit) {
                        options.push({
                            label: 'Rename template',
                            icon: <PencilRegularRenameIcon size={18} />,
                            handleSelect: () => {
                                setSequenceTemplateId(cell.value.id);
                                setSequenceTemplateName(cell.value.name);
                                setShouldShowRenameModal(true);
                            },
                        });
                    }

                    options.push({
                        label: cell.value.canEdit ? 'Edit template' : 'View template',
                        icon: <PencilOutlineIcon size={18} />,
                        handleSelect: () => {
                            setSequenceTemplateId(cell.value.id);
                            setShouldShowModal(true);
                        },
                    });

                    if (canChangeTemplateGroup(user, cell.value, cell.value.parent)) {
                        options.push({
                            label: 'Change group',
                            icon: <EmptyFolderIcon size={16} />,
                            handleSelect: () => {
                                setShouldShowChangeGroupModal(true);
                                setSequenceTemplateId(cell.value.id);
                                setTemplateGroupId(cell.value.group ? cell.value.group.id : 'null');
                            },
                        });
                    }

                    if (cell.value.lastSentTime) {
                        options.push(
                            {
                                label: 'View all people recipients',
                                icon: <PeopleRegularIcon size={18} />,
                                handleSelect: () => {
                                    appHistory.push(
                                        `/people?${getSerializedFilter(
                                            cell.value.id
                                        )}&persist=false`
                                    );
                                },
                            },
                            {
                                label: 'View all lead recipients',
                                icon: <LeadChevronRegularIcon size={18} />,
                                handleSelect: () => {
                                    appHistory.push(
                                        `/leads?${getSerializedFilter(cell.value.id)}&persist=false`
                                    );
                                },
                            }
                        );
                    }
                    options.push({
                        label: 'Duplicate template',
                        icon: <CopyRegularIcon size={18} />,
                        handleSelect: () => {
                            setTemplateToDuplicate({id: cell.value.id, name: cell.value.name});
                        },
                    });

                    if (cell.value.canEdit) {
                        options.push({
                            label: 'Archive template',
                            icon: <TrashRegularIcon size={18} />,
                            variant: 'destructive',
                            handleSelect: () => setIsConfirmingArchiveId(cell.value.id),
                        });
                    }

                    return (
                        <div className={styles['action-menu-container']}>
                            <ActionMenu
                                getRef={(node) => {
                                    // @ts-expect-error
                                    menuRef.current = node;
                                }}
                                // @ts-expect-error
                                options={options}
                                size='small'
                            />
                        </div>
                    );
                },
            },
        ],
        [
            fullFragmentLoading,
            setSequenceTemplateId,
            setShouldShowModal,
            setSequenceTemplateName,
            setShouldShowRenameModal,
            setShouldShowChangeGroupModal,
            setTemplateGroupId,
            user,
        ]
    );

    return (
        <>
            <LegacySetupTableContainer>
                <Table
                    firstRow={
                        <AddButtonRow buttonText='Create new template' onClick={onAddRowClick} />
                    }
                    isStriped={true}
                    isResizable={false}
                    hasHeaderBorder={true}
                    hasHeaderCellBorders={true}
                    headerBackgroundColor='offWhite'
                    isCompact={true}
                    stickyHeaders={false}
                    hasBorders={true}
                    hasCellBorders={false}
                    isCheckboxSelectable={false}
                    fullWidth={true}
                    disableFilters={true}
                    shouldShowFooter={false}
                    // @ts-expect-error
                    columns={columns}
                    data={props.templates}
                    sortBy={
                        props.templates.length
                            ? [
                                  {
                                      id: 'lastSentTime',
                                      desc: true,
                                  },
                              ]
                            : undefined
                    }
                    specialStates={[
                        {
                            shouldRender: () => Boolean(props.errorMessage),
                            component: <ErrorState errorMessage={props.errorMessage} />,
                        },
                        {
                            shouldRender: () => props.isLoading && !props.templates.length,
                            component: <LoadingPage padding={32} />,
                        },
                    ]}
                />
            </LegacySetupTableContainer>
            {isConfirmingArchiveId && menuRef.current ? (
                <Dialog
                    anchor={menuRef.current}
                    location='top'
                    question='Are you sure you want to archive this template?'
                    primaryText='Yes, archive'
                    secondaryText='Cancel'
                    onPrimary={() => {
                        onDelete(isConfirmingArchiveId);
                        closeConfirmationDialog();
                    }}
                    onSecondary={closeConfirmationDialog}
                    isWarning={true}
                />
            ) : undefined}
            {templateToDuplicate && (
                <SingleInputModal
                    title='Make a copy'
                    subtitle='Name a copy of this template'
                    errorMessage={duplicateErrorMessage}
                    initialValue={`Copy of ${templateToDuplicate.name}`}
                    buttonText='Make a copy'
                    onClose={closeDuplicateModal}
                    isSaving={isDuplicating}
                    onSave={handleDuplicateTemplate}
                />
            )}
            {isShowingSequenceOnboarding ? (
                <HelpNutshellEmailMarketing
                    isOpen={true}
                    onClose={() => setIsShowingSequenceOnboarding(false)}
                />
            ) : undefined}
        </>
    );
}
